import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Sequential segment playback. An episode is a playlist, not a file, so the
 * player is the thing that makes 8s shots read as one show.
 *
 * TWO ELEMENTS, A/B SWAPPED ON `ended`, WITH THE NEXT SEGMENT ALREADY LOADED.
 *
 * The load-bearing detail is the unlock. iOS Safari grants playback
 * permission PER ELEMENT PER USER GESTURE. Calling B.play() from A's `ended`
 * callback is not inside a gesture, so it throws NotAllowedError and playback
 * dies silently after one segment. So the tap handler calls .play() on BOTH
 * elements synchronously — that is what mints B's permission — and then
 * pauses B the moment its play promise settles.
 *
 * A single element with a changing src would sidestep the unlock, but it
 * re-buffers between every 8s shot, which is exactly the stutter a 2-year-old
 * walks away from.
 *
 * `autoStart` IS THE SAME STORY TOLD ONE TAP EARLIER, and it exists because
 * of a real complaint: "when we click to play/view a video, we shouldn't have
 * to click twice". On the child's shelf the first tap picked the film and the
 * second one — this component's ▶︎ button — was the gesture the browser
 * demanded, because minting the playlist is a network round trip and the
 * <video> elements did not even exist when the tile was tapped.
 *
 * With `autoStart` the caller mounts this component INSIDE the tap (see
 * ChildMode's flushSync), and two things follow:
 *
 *   1. mount with an empty playlist calls play() on both elements while the
 *      gesture is still on the stack. That call rejects — there is no src yet
 *      — and it does not matter: WebKit lifts the element's user-gesture
 *      restriction on the way IN to play(), before it looks for a source, and
 *      the lift is permanent for that element. It is the same property the
 *      A/B swap above already leans on when it re-srcs a finished element and
 *      plays it from a callback.
 *   2. when the playlist lands, playback starts on its own.
 *
 * IF THAT UNLOCK DOES NOT HOLD, THE BUTTON COMES BACK rather than the screen
 * staying black — a refused autostart sets `blocked` and re-offers the tap,
 * which is exactly the two-tap behaviour we had before. Deleting the button
 * outright would trade an extra tap for a black rectangle, which is far worse.
 * The other tempting fix, starting muted (browsers always allow that), is not
 * one: these are films with dialogue and music, and a parent who hears nothing
 * assumes the product is broken.
 */
export function Player({
  urls,
  onExit,
  startLabel = '▶︎ Play',
  autoStart = false,
}: {
  urls: string[];
  onExit: () => void;
  startLabel?: string;
  autoStart?: boolean;
}) {
  const refA = useRef<HTMLVideoElement>(null);
  const refB = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  /** The first frame has actually painted, as opposed to "we called play()". */
  const [rolling, setRolling] = useState(false);
  const [live, setLive] = useState<0 | 1>(0);
  const [index, setIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  /** An unattended start was refused, so the tap has to be asked for after all. */
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const primed = useRef(false);
  const autoTried = useRef(false);

  const elementAt = (which: 0 | 1) => (which === 0 ? refA.current : refB.current);

  const loadFromStart = useCallback(() => {
    const a = refA.current;
    const b = refB.current;
    if (a) {
      if (urls[0]) a.src = urls[0];
      else a.removeAttribute('src');
      // FROM THE BEGINNING, EXPLICITLY. Assigning src re-runs the load
      // algorithm and would reset this on its own, but only when the URL
      // actually changes — re-entering the same film hands back the same
      // minted URL, and an element that is still holding it would otherwise
      // resume wherever the last viewing stopped.
      a.currentTime = 0;
    }
    if (b) {
      if (urls[1]) b.src = urls[1];
      else b.removeAttribute('src');
      b.currentTime = 0;
    }
    setIndex(0);
    setLive(0);
  }, [urls]);

  // Preload the first two segments so the tap has something to unlock.
  //
  // A LAYOUT effect, not a passive one: with `autoStart` the effect below
  // plays these elements in the same commit — inside the tap — and it must not
  // find them empty.
  useLayoutEffect(() => {
    setStarted(false);
    setRolling(false);
    setFinished(false);
    setBlocked(false);
    setError(null);
    autoTried.current = false;
    loadFromStart();
  }, [loadFromStart]);

  const start = useCallback(
    (auto = false) => {
      const a = refA.current;
      if (!a || !urls.length) return;
      if (finished || index !== 0) {
        // Replaying: rewind both elements BEFORE the play() calls, so both stay
        // inside this gesture.
        loadFromStart();
      } else {
        // Every other way in also means "from the top" — a first tap, and the
        // tap offered after a refused autostart — and this used to read an
        // index of 0 as a playhead of 0. A Watch screen was seen in Chrome
        // holding its first segment at 8.00 of 8.00 after an autostart it had
        // not been allowed to make; what moved it was never pinned down, but
        // pressing ▶︎ in that state ends the segment instead of playing it.
        // Seeking to 0 costs nothing when it is already there, and re-assigning
        // src to force it would throw away the preload this player exists for.
        a.currentTime = 0;
      }
      const b = refB.current;

      setStarted(true);
      setFinished(false);
      setBlocked(false);

      // BOTH .play() calls must happen synchronously inside this handler.
      const playA = a.play();
      const playB = b && b.getAttribute('src') ? b.play() : null;

      playA?.catch((err: unknown) => {
        if (auto) {
          // ONLY A REFUSAL PUTS THE BUTTON BACK, and the distinction is the
          // whole of this branch. `if (auto)` used to catch EVERY rejection:
          //
          //   NotAllowedError  the browser refused to start on its own. Real,
          //                    expected on iOS, and correctly handled by
          //                    re-offering the tap.
          //   AbortError       the play was SUPERSEDED — by the pause below,
          //                    or by a new segment loading. The film is
          //                    playing, or is about to. Treating it as a
          //                    refusal set blocked=true over a film that was
          //                    running: a tap target laid across the picture,
          //                    which a two-year-old will press, stopping the
          //                    thing they were watching.
          //
          // Named rather than matched on the message, because the message is
          // browser-specific prose and the name is spec'd on DOMException.
          const name = (err as { name?: string } | null)?.name;
          if (name === 'NotAllowedError' || name === 'SecurityError') {
            setStarted(false);
            setBlocked(true);
          }
          // Anything else on the auto path is swallowed on purpose. An
          // autostart is a courtesy — if it fails for a reason that is not a
          // refusal, the tap the child is about to make still works, and a
          // toddler cannot read an error either way.
          return;
        }
        setError(String(err));
      });
      // B is now unlocked for the rest of the session; park it at the start of
      // its segment. Swallow the AbortError that pausing a pending play throws.
      playB
        ?.then(() => {
          if (b) {
            b.pause();
            b.currentTime = 0;
          }
        })
        .catch(() => {
          /* unlock attempt only; a failure costs a re-buffer, not the session */
        });
    },
    [finished, index, loadFromStart, urls.length],
  );

  /**
   * Mint the per-element permission while the caller's tap is still on the
   * stack, for the case where there is nothing to play yet.
   *
   * THE PAUSE IS SYNCHRONOUS, AND THAT IS THE WHOLE POINT. Parking on the play
   * promise the way start() parks B looks equivalent and is not: play() on an
   * element with no source does not reject, it WAITS for a source. Setting the
   * src 700ms later and starting the film resolves that original promise too,
   * so a `.then(pause)` hanging off it stops the film a frame after it starts.
   * Measured in Chrome against a 700ms mint: play() at +712ms, first frame,
   * then paused at 0.00s with the segment counter showing — one tap to reach a
   * film that never moves, which is worse than the two taps we started with.
   * Pausing here instead rejects the pending promise with AbortError, which is
   * why it is swallowed.
   */
  const prime = useCallback(() => {
    if (primed.current) return;
    primed.current = true;
    for (const el of [refA.current, refB.current]) {
      if (!el) continue;
      const attempt = el.play();
      el.pause();
      attempt?.catch(() => {
        /* AbortError from the pause above, or NotSupportedError where the
           browser gives up on a sourceless element at once. The call is here
           for the restriction it lifts, not for the playback it cannot do. */
      });
    }
  }, []);

  // The one-tap path. Layout, and declared after the preload above, so that
  // within a single commit the srcs are set first and played second.
  useLayoutEffect(() => {
    if (!autoStart) return;
    if (!urls.length) {
      prime();
      return;
    }
    if (autoTried.current) return;
    autoTried.current = true;
    start(true);
  }, [autoStart, urls, prime, start]);

  const handleEnded = useCallback(
    (which: 0 | 1) => {
      if (which !== live || !started) return; // the parked element can fire too
      const next = index + 1;
      if (next >= urls.length) {
        setFinished(true);
        return;
      }
      const other: 0 | 1 = which === 0 ? 1 : 0;
      const otherEl = elementAt(other);
      const endedEl = elementAt(which);

      if (otherEl) {
        // Already holds urls[next] and was unlocked in the original gesture.
        otherEl.currentTime = 0;
        void otherEl.play().catch((err: unknown) => setError(String(err)));
      }
      setLive(other);
      setIndex(next);

      // The element that just finished becomes the buffer for next + 1.
      const after = urls[next + 1];
      if (endedEl) {
        if (after) {
          endedEl.src = after;
          endedEl.load();
        } else {
          endedEl.removeAttribute('src');
        }
      }
    },
    [index, live, started, urls],
  );

  // With `autoStart` the button is a FALLBACK, not the way in: it appears only
  // once the film is over, or once an unattended start has actually been
  // refused. Rendering it while we are still starting would put the second tap
  // back on screen, which is the whole bug.
  const showTap = finished || (autoStart ? blocked : !started);
  const waiting = autoStart && !rolling && !showTap && !error;

  return (
    <div className="player">
      <button className="exit" onClick={onExit} aria-label="close">
        ×
      </button>
      {started && !finished && (
        <div className="seg">
          {/* HIDDEN FOR A SINGLE-SEGMENT FILM. "segment 1 / 1" is our
              playlist plumbing leaking onto a parent's screen — it told them
              nothing and invited the question Jamie asked: "why do we have
              segment 1/1 in top right?". A real multi-part episode still
              shows progress, because there it means something. */}
          {urls.length > 1 && <>segment {index + 1} / {urls.length}</>}
        </div>
      )}

      <video
        ref={refA}
        className={live === 0 && started && !finished ? 'live' : ''}
        playsInline
        preload="auto"
        onPlaying={() => setRolling(true)}
        onEnded={() => handleEnded(0)}
        onError={() => setError('a segment failed to load (element A)')}
      />
      <video
        ref={refB}
        className={live === 1 && started && !finished ? 'live' : ''}
        playsInline
        preload="auto"
        onPlaying={() => setRolling(true)}
        onEnded={() => handleEnded(1)}
        onError={() => setError('a segment failed to load (element B)')}
      />

      {/* Something on screen for the whole gap between the tap and the first
          frame — minting the playlist, then buffering. Deliberately the same
          glyph and size as the caller's own loading face, so the handover from
          one to the other is not a flicker the child can see. */}
      {waiting && (
        <div className="player-wait" role="status" aria-label="starting the film">
          …
        </div>
      )}

      {showTap && (
        <button className="tapstart" onClick={() => start()}>
          {finished ? '↻ Again' : startLabel}
        </button>
      )}
      {error && (
        <div className="err" style={{ position: 'absolute', bottom: 16, left: 16, right: 16 }}>
          {error}
        </div>
      )}
    </div>
  );
}
